/**
 * Currency Selector Manager - Moon Moon Agency
 * Ouverture/fermeture du selecteur de devise du footer (snippet mm-currency-selector).
 *
 * Un seul <dialog> pour deux comportements :
 * - desktop : show() — popover non modal, ferme au clic exterieur et sur Escape
 *   (un dialog non modal ne gere pas Escape nativement) ;
 * - mobile (<= SMALL_DESKTOP_BREAKPOINT, breakpoint du footer) : showModal() —
 *   drawer avec voile, focus trap et Escape natifs, scroll de fond verrouille.
 *
 * Le changement de devise lui-meme est un submit natif du formulaire localization.
 */

/*********************************************************************************************
 * 💱 CURRENCY SELECTOR MANAGER
 */
const CurrencySelectorManager = {
	config: {
		selectors: {
			open: '[data-mm-currency-open]',
			dialog: '[data-mm-currency-dialog]',
			close: '[data-mm-currency-close]',
		},
		classes: {
			noScroll: 'stop-scrolling',
		},
	},

	_bound: false,

	init() {
		// Guard : le snippet peut etre re-rendu (editeur de theme), on ne lie qu'une fois
		if (this._bound || !window.getElement(this.config.selectors.dialog)) return;
		this._bound = true;

		this._bindEvents();
		window.log('info', '💱 [CURRENCY_SELECTOR]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.open);
			if (trigger) {
				this.toggle(trigger);
				return;
			}

			const dialog = this._getOpenDialog();
			if (!dialog) return;

			/* Croix, clic sur le ::backdrop (cible = le <dialog> lui-meme) ou clic
			   hors du popover desktop. */
			if (e.target.closest(this.config.selectors.close) || e.target === dialog || !dialog.contains(e.target)) {
				dialog.close();
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key !== 'Escape') return;
			const dialog = this._getOpenDialog();
			if (dialog && !dialog.matches(':modal')) dialog.close();
		});

		/* L'evenement `close` ne bubble pas mais traverse la phase de capture :
		   un seul listener couvre croix, voile, Escape natif et clic exterieur. */
		document.addEventListener('close', (e) => {
			if (!e.target.matches(this.config.selectors.dialog)) return;
			document.body.classList.remove(this.config.classes.noScroll);
			window.getElement(`[aria-controls="${e.target.id}"]`)?.setAttribute('aria-expanded', 'false');
		}, true);

		/* Bascule de breakpoint panneau ouvert : le mode (popover / modal) ne
		   correspond plus au CSS, on referme. */
		window.matchMedia(`(width <= ${window.SMALL_DESKTOP_BREAKPOINT}px)`)
			.addEventListener('change', () => this._getOpenDialog()?.close());
	},

	_getOpenDialog() {
		return window.getElement(`${this.config.selectors.dialog}[open]`);
	},

	toggle(trigger) {
		const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
		if (!dialog || typeof dialog.showModal !== 'function') return;

		dialog.open ? dialog.close() : this.open(dialog, trigger);
	},

	open(dialog, trigger) {
		if (window.isSmallDesktop()) {
			dialog.showModal();
			document.body.classList.add(this.config.classes.noScroll);
		} else {
			dialog.show();
		}

		trigger.setAttribute('aria-expanded', 'true');
		window.emit('global:currency-selector-opened');
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => CurrencySelectorManager.init());
} else {
	CurrencySelectorManager.init();
}
document.addEventListener('shopify:section:load', () => CurrencySelectorManager.init());
